import { useCallback } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useWorkQueueContext } from '../context/useWorkQueueContext.js';
import { taskActionErrorToast } from '../utils/taskActionToast.js';
import { toUiOwnerFromAssignee, currentUserUuid } from '../utils/taskOwner.js';
import { escalateParamsFromEvent, severityFromPriority } from '../utils/escalateParams.js';
import { escalateEntity } from '../../../services/index.js';
import { INTERIM_EVENT_TYPE } from '../constants/interimEventTypes.js';

/**
 * Escalate a work-queue event to a single assignee (CT-5094). Escalate is
 * entity-keyed — it creates (or reassigns) an open task via the entity escalate
 * endpoints (services/escalations.js), NOT task/reassign. For a `task` event the
 * owner changes optimistically and rolls back on failure; for an entity event
 * (change_request / claim / proposal) a task is created, so there is no owner to
 * update. Rollback + error toast follow the CT-5093 pattern.
 *
 * @returns {(event: Object, form: { assignee: {id:string,name:string}, reason: string,
 *   priority: 'normal'|'high', message: string }) => Promise<boolean>}
 *   resolves `true` when the escalate succeeded (caller shows the success toast).
 */
export function useEscalateAction() {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const { currentPersona, updateEventOwner, appendAuditEntry, userNameById } = useWorkQueueContext();

  return useCallback(
    async (event, { assignee, reason, priority, message }) => {
      const params = escalateParamsFromEvent(event);
      if (!params || !assignee?.id) {
        toast.error({ title: t('escalate.failedTitle'), message: t('escalate.failedMessage') });
        return false;
      }

      const isTask = event?.type === INTERIM_EVENT_TYPE.TASK;
      const prevOwner = event.owner;
      const me = currentUserUuid();
      const nextOwner = toUiOwnerFromAssignee(assignee.id, {
        nameById: { ...userNameById, [assignee.id]: assignee.name },
        viewerUuid: me,
      });
      if (isTask && nextOwner) updateEventOwner(event.id, nextOwner);

      // DEV-ONLY seed tasks skip the network (a fake entityUuid would 404) so the
      // graphical flow shows a clean optimistic success (mirrors reassign/resolve).
      const auditActor = currentPersona.name;

      if (event.detail?.__seed) {
        appendAuditEntry(event.id, {
          id: `escalate-${Date.now()}`,
          actor: auditActor,
          action: t('escalate.auditEscalated', { assignee: assignee.name }),
          timeAgo: t('team.justNow'),
        });
        return true;
      }

      try {
        const body = await escalateEntity({
          ...params,
          assigneeUuid: assignee.id,
          severity: severityFromPriority(priority),
          reason,
          message,
        });
        if (!body) {
          if (isTask) updateEventOwner(event.id, prevOwner);
          toast.error({ title: t('escalate.failedTitle'), message: t('escalate.failedMessage') });
          return false;
        }
        appendAuditEntry(event.id, {
          id: `escalate-${Date.now()}`,
          actor: auditActor,
          action: t('escalate.auditEscalated', { assignee: assignee.name }),
          timeAgo: t('team.justNow'),
        });
        return true;
      } catch (err) {
        if (isTask) updateEventOwner(event.id, prevOwner);
        const { title, message: errMsg } = taskActionErrorToast(t, err, 'escalate');
        toast.error({ title, message: errMsg });
        return false;
      }
    },
    [appendAuditEntry, currentPersona, t, toast, updateEventOwner, userNameById],
  );
}
